import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Upload } from "lucide-react";
import { api, API, inr, formatApiErrorDetail } from "@/lib/api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const EMPTY = {
  name: "",
  brand: "Physio Essential",
  category: "bone",
  price: "",
  compare_price: "",
  short_desc: "",
  description: "",
  benefits: "",
  ingredients: "",
  info_images: "",
  image: "https://images.unsplash.com/photo-1763668331599-487470fb85b2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxwcmVtaXVtJTIwc3VwcGxlbWVudCUyMGJvdHRsZSUyMG1vY2t1cHxlbnwwfHx8fDE3ODU2NzA4MDV8MA&ixlib=rb-4.1.0&q=85",
  stock: "100",
  servings: "30 servings",
  featured: false,
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("File too large (max 8 MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload?kind=product", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // API returns url like "/api/files/<path>" — prepend backend origin for absolute serving
      const url = data.url.startsWith("http") ? data.url : `${API}${data.url.replace(/^\/api/, "")}`;
      setForm((f) => ({ ...f, image: url }));
      toast.success("Image uploaded");
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex?.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const load = () => api.get("/products", { params: { sort: "featured" } }).then(({ data }) => setProducts(data));
  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      ...p,
      price: String(p.price),
      compare_price: p.compare_price ? String(p.compare_price) : "",
      stock: String(p.stock),
      benefits: (p.benefits || []).join("\n"),
      ingredients: (p.ingredients || []).join("\n"),
      info_images: (p.info_images || []).join("\n"),
    });
    setOpen(true);
  };

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.name || !form.price || !form.short_desc) {
      toast.error("Name, price and short description are required");
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name,
      brand: form.brand || "Physio Essential",
      category: form.category,
      price: parseFloat(form.price),
      compare_price: form.compare_price ? parseFloat(form.compare_price) : null,
      short_desc: form.short_desc,
      description: form.description || form.short_desc,
      benefits: form.benefits.split("\n").map((s) => s.trim()).filter(Boolean),
      ingredients: form.ingredients.split("\n").map((s) => s.trim()).filter(Boolean),
      info_images: form.info_images.split("\n").map((s) => s.trim()).filter(Boolean),
      image: form.image,
      stock: parseInt(form.stock) || 0,
      servings: form.servings,
      featured: !!form.featured,
      rating: editing?.rating ?? 4.8,
      reviews_count: editing?.reviews_count ?? 0,
      tags: editing?.tags ?? [],
    };
    try {
      if (editing) {
        await api.put(`/admin/products/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Product created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/products/${deleteId}`);
      toast.success("Product deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <div className="p-6 sm:p-10" data-testid="admin-products">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-mono text-xs uppercase tracking-overline text-[#2F80ED]">Inventory</p>
          <h1 className="mt-2 font-display text-3xl font-bold tracking-tight">Products</h1>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 bg-black px-5 py-3 font-semibold text-white transition-colors hover:bg-[#2F80ED]"
          data-testid="add-product-btn"
        >
          <Plus className="h-4 w-4" /> Add Product
        </button>
      </div>

      <div className="mt-8 border border-black/10 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Stock</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.map((p) => (
              <TableRow key={p.id} data-testid={`product-row-${p.slug}`}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="h-11 w-10 overflow-hidden bg-[#F3F4F6]">
                      <img src={p.image} alt={p.name} className="h-full w-full object-cover" />
                    </div>
                    <span className="font-medium">{p.name}</span>
                  </div>
                </TableCell>
                <TableCell className="font-mono text-xs uppercase">{p.category}</TableCell>
                <TableCell className="font-mono">{inr(p.price)}</TableCell>
                <TableCell className={`font-mono font-bold ${p.stock < 20 ? "text-red-500" : ""}`}>{p.stock}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => openEdit(p)}
                      className="flex h-9 w-9 items-center justify-center border border-black/15 hover:bg-black hover:text-white"
                      data-testid={`edit-${p.slug}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => setDeleteId(p.id)}
                      className="flex h-9 w-9 items-center justify-center border border-black/15 hover:bg-red-500 hover:text-white hover:border-red-500"
                      data-testid={`delete-${p.slug}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto bg-white" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit Product" : "New Product"}</DialogTitle>
            <DialogDescription className="font-mono text-[11px] uppercase tracking-overline">
              {editing ? "Update supplement details" : "Add a new supplement to the catalog"}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label className="mb-1.5 block font-mono text-[11px] uppercase tracking-overline text-black/50">Name</Label>
              <Input value={form.name} onChange={upd("name")} data-testid="form-name" />
            </div>
            <div className="col-span-2">
              <Label className="mb-1.5 block font-mono text-[11px] uppercase tracking-overline text-black/50">Brand</Label>
              <Input value={form.brand} onChange={upd("brand")} data-testid="form-brand" />
            </div>
            <div>
              <Label className="mb-1.5 block font-mono text-[11px] uppercase tracking-overline text-black/50">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="form-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bone">Bone Health</SelectItem>
                  <SelectItem value="muscle">Muscle Recovery</SelectItem>
                  <SelectItem value="joint">Joint Health</SelectItem>
                  <SelectItem value="nerve">Nerve Health</SelectItem>
                  <SelectItem value="sports">Sports Recovery</SelectItem>
                  <SelectItem value="geriatric">Geriatric Mobility</SelectItem>
                  <SelectItem value="postsurgery">Post-Surgery Recovery</SelectItem>
                  <SelectItem value="wellness">Daily Wellness</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block font-mono text-[11px] uppercase tracking-overline text-black/50">Servings</Label>
              <Input value={form.servings} onChange={upd("servings")} data-testid="form-servings" />
            </div>
            <div>
              <Label className="mb-1.5 block font-mono text-[11px] uppercase tracking-overline text-black/50">Price (₹)</Label>
              <Input type="number" value={form.price} onChange={upd("price")} data-testid="form-price" />
            </div>
            <div>
              <Label className="mb-1.5 block font-mono text-[11px] uppercase tracking-overline text-black/50">Compare Price (₹)</Label>
              <Input type="number" value={form.compare_price} onChange={upd("compare_price")} data-testid="form-compare" />
            </div>
            <div>
              <Label className="mb-1.5 block font-mono text-[11px] uppercase tracking-overline text-black/50">Stock</Label>
              <Input type="number" value={form.stock} onChange={upd("stock")} data-testid="form-stock" />
            </div>
            <div className="flex items-end pb-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.featured}
                  onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))}
                  data-testid="form-featured"
                  className="h-4 w-4 accent-[#2F80ED]"
                />
                Featured product
              </label>
            </div>
            <div className="col-span-2">
              <Label className="mb-1.5 block font-mono text-[11px] uppercase tracking-overline text-black/50">Product Image</Label>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                {form.image && (
                  <img
                    src={form.image}
                    alt="preview"
                    className="h-24 w-24 shrink-0 border border-black/10 bg-white object-contain p-1"
                  />
                )}
                <div className="flex-1 space-y-2">
                  <Input value={form.image} onChange={upd("image")} placeholder="Paste an image URL, or upload below" data-testid="form-image" />
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="flex items-center gap-2 border border-black/15 bg-white px-4 py-2 font-mono text-[11px] uppercase tracking-overline text-black hover:bg-black hover:text-white disabled:opacity-50"
                      data-testid="upload-image-btn"
                    >
                      {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                      {uploading ? "Uploading…" : "Upload from device"}
                    </button>
                    <span className="text-[11px] text-black/40">JPG, PNG, WebP · Max 8 MB</span>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="hidden"
                    onChange={uploadImage}
                    data-testid="upload-image-input"
                  />
                </div>
              </div>
            </div>
            <div className="col-span-2">
              <Label className="mb-1.5 block font-mono text-[11px] uppercase tracking-overline text-black/50">Short Description</Label>
              <Input value={form.short_desc} onChange={upd("short_desc")} data-testid="form-short" />
            </div>
            <div className="col-span-2">
              <Label className="mb-1.5 block font-mono text-[11px] uppercase tracking-overline text-black/50">Full Description</Label>
              <Textarea value={form.description} onChange={upd("description")} data-testid="form-desc" />
            </div>
            <div>
              <Label className="mb-1.5 block font-mono text-[11px] uppercase tracking-overline text-black/50">Benefits (one/line)</Label>
              <Textarea value={form.benefits} onChange={upd("benefits")} data-testid="form-benefits" />
            </div>
            <div>
              <Label className="mb-1.5 block font-mono text-[11px] uppercase tracking-overline text-black/50">Ingredients (one/line)</Label>
              <Textarea value={form.ingredients} onChange={upd("ingredients")} data-testid="form-ingredients" />
            </div>
            <div className="col-span-2">
              <Label className="mb-1.5 block font-mono text-[11px] uppercase tracking-overline text-black/50">Info / Benefit Image URLs (one per line)</Label>
              <Textarea value={form.info_images} onChange={upd("info_images")} data-testid="form-info-images" placeholder="/products/info/example.jpg" />
            </div>
          </div>

          <DialogFooter>
            <button onClick={() => setOpen(false)} className="border border-black/15 px-5 py-2.5 font-semibold hover:bg-black/5">
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="flex items-center gap-2 bg-black px-6 py-2.5 font-semibold text-white hover:bg-[#2F80ED] disabled:opacity-60"
              data-testid="save-product-btn"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {editing ? "Update" : "Create"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this product?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-500 hover:bg-red-600" data-testid="confirm-delete-btn">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
